import { useState } from 'react';
import { CircleAlert, Globe2, MapPinned } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog';
import { MultiSelectCombobox } from '@/shared/components/ui/multi-select-combobox';
import { StateCountiesEditor } from './StateCountiesEditor';
import { useSharedStates } from './useSharedGeography';
import type { CoverageScope, SpecialtyCoverageState } from './types';

type CoverageControlProps = {
  specialtyName: string;
  coverageScope: CoverageScope | null;
  coverageStates: SpecialtyCoverageState[];
  onChange: (scope: CoverageScope, states: SpecialtyCoverageState[]) => void;
  disabled?: boolean;
};

/** Human-readable coverage summary, e.g. "Nationwide" or "MA, NH +2". */
function coverageSummary(scope: CoverageScope | null, states: SpecialtyCoverageState[]): string {
  if (scope === null) return 'Not configured';
  if (scope === 'NATIONAL') return 'Nationwide';
  const codes = states.map((state) => state.stateCode);
  if (codes.length === 0) return 'No states selected';
  if (codes.length <= 3) return codes.join(', ');
  return `${codes.slice(0, 2).join(', ')} +${codes.length - 2} more`;
}

export function CoverageControl({
  specialtyName,
  coverageScope,
  coverageStates,
  onChange,
  disabled = false,
}: CoverageControlProps) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<CoverageScope | null>(coverageScope);
  const [states, setStates] = useState(coverageStates);
  const { data: stateOptions = [], isLoading: statesLoading } = useSharedStates();

  const Icon = coverageScope === null ? CircleAlert : coverageScope === 'NATIONAL' ? Globe2 : MapPinned;
  const summary = coverageSummary(coverageScope, coverageStates);
  const summaryA11y = coverageScope === null ? 'Not configured' : coverageScope === 'NATIONAL' ? 'National' : summary;

  const regionalInvalid =
    scope === 'REGIONAL' &&
    (states.length === 0 ||
      states.some(({ allCounties, countyIds }) => !allCounties && countyIds.length === 0));

  const applyDisabled = scope === null || regionalInvalid;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setScope(coverageScope);
      setStates(coverageStates);
    }
    setOpen(nextOpen);
  };

  const handleStatesChange = (codes: string[]) => {
    setStates((current) =>
      codes.map(
        (code) =>
          current.find((state) => state.stateCode === code) ?? {
            stateCode: code,
            allCounties: true,
            countyIds: [],
          },
      ),
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="group mt-0.5 inline-flex items-center gap-1.5 rounded-sm text-sm text-sage focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={`Coverage: ${summaryA11y}`}
          title={`Edit coverage — ${summary}`}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="underline decoration-dotted underline-offset-4 group-hover:decoration-solid">
            {summary}
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="flex h-[min(760px,90vh)] max-w-2xl flex-col gap-0">
        <DialogHeader>
          <DialogTitle className="font-recoleta-medium text-xl font-normal text-text-primary">
            {specialtyName} coverage
          </DialogTitle>
          <DialogDescription>
            Choose national coverage or configure states and counties.
          </DialogDescription>
        </DialogHeader>

        <div
          className="mt-5 grid grid-cols-2 gap-0.5 rounded-lg bg-card-subtle p-1"
          role="radiogroup"
          aria-label="Coverage scope"
        >
          {(['NATIONAL', 'REGIONAL'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={scope === option}
              onClick={() => setScope(option)}
              className={cn(
                'rounded-md py-2 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage',
                scope === option
                  ? 'bg-white font-medium text-sage shadow-sm'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              {option === 'NATIONAL' ? 'National' : 'Regional'}
            </button>
          ))}
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          {scope === 'NATIONAL' ? (
            <div className="flex items-start gap-3 rounded-lg bg-sage-bg px-4 py-3 text-sm text-text-secondary">
              <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-sage" />
              <p>
                This specialty serves clients nationwide. State and county restrictions do not
                apply.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-text-primary">
                  Coverage states
                </label>
                <MultiSelectCombobox
                  options={stateOptions.map(({ code, name }) => ({ id: code, name }))}
                  value={states.map(({ stateCode }) => stateCode)}
                  onChange={handleStatesChange}
                  loading={statesLoading}
                  placeholder="Select states"
                  searchPlaceholder="Search states…"
                  emptyText="No states found"
                  maxVisibleBadges={4}
                />
              </div>

              {states.map((state) => {
                const option = stateOptions.find(({ code }) => code === state.stateCode);
                return (
                  <StateCountiesEditor
                    key={state.stateCode}
                    stateCode={state.stateCode}
                    stateName={option?.name ?? state.stateCode}
                    allCounties={state.allCounties}
                    countyIds={state.countyIds}
                    onToggleAllCounties={(allCounties) =>
                      setStates((current) =>
                        current.map((row) =>
                          row.stateCode === state.stateCode
                            ? { ...row, allCounties, countyIds: allCounties ? [] : row.countyIds }
                            : row,
                        ),
                      )
                    }
                    onCountiesChange={(countyIds) =>
                      setStates((current) =>
                        current.map((row) =>
                          row.stateCode === state.stateCode
                            ? { ...row, allCounties: false, countyIds }
                            : row,
                        ),
                      )
                    }
                  />
                );
              })}
              {regionalInvalid && (
                <p className="text-sm text-red-600" role="alert">
                  Select at least one state and one county per state when All counties is off.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 border-t border-border-subtle pt-4">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={applyDisabled}
            className="bg-bquest-green text-white hover:bg-bquest-green/90"
            onClick={() => {
              if (scope === null) return;
              onChange(scope, scope === 'NATIONAL' ? [] : states);
              setOpen(false);
            }}
          >
            Apply coverage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
